import React, { useMemo } from "react";
import { useAuthStore } from "@/stores/authStore";
import type { UserRole } from "@repo/schemas/permissionSchema";
import type {
  Permission,
  PermissionCheck,
  PermissionRule,
} from "@repo/schemas/permissionSchema";

// Extended UserRole type to support dynamic roles
type ExtendedUserRole = {
  id: string;
  name: UserRole;
  description: string;
};

// Updated User type to support dynamic role
interface ExtendedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: {
    id: string;
    name: UserRole;
    description: string;
  };
  company?: {
    id: string;
    name: string;
  } | null;
  companyId?: string | null;
}

type User = ExtendedUser;

/**
 * Simplified Permission Manager that works directly with permission arrays
 */
export class PermissionManager {
  /**
   * Check if permissions array contains a specific permission
   */
  static hasPermission(
    permissions: string[] | null | undefined,
    requiredPermission: Permission
  ): boolean {
    if (!permissions) return false;
    return permissions.includes(requiredPermission);
  }

  /**
   * Check if permissions array contains ANY of the specified permissions (OR logic)
   */
  static hasAnyPermission(
    permissions: string[] | null | undefined,
    permissionsToCheck: Permission[]
  ): boolean {
    if (!permissions) return false;
    return permissionsToCheck.some((permission) =>
      permissions.includes(permission)
    );
  }

  /**
   * Check if permissions array contains ALL of the specified permissions (AND logic)
   */
  static hasAllPermissions(
    permissions: string[] | null | undefined,
    permissionsToCheck: Permission[]
  ): boolean {
    if (!permissions) return false;
    return permissionsToCheck.every((permission) =>
      permissions.includes(permission)
    );
  }

  /**
   * Enhanced permission checking with complex rules
   */
  static checkPermission(
    permissions: string[] | null | undefined,
    check: PermissionCheck
  ): boolean {
    if (!permissions) return false;

    // Simple permission string
    if (typeof check === "string") {
      return this.hasPermission(permissions, check);
    }

    // Array of permissions (OR logic by default)
    if (Array.isArray(check)) {
      return this.hasAnyPermission(permissions, check);
    }

    // Complex permission rule
    const rule = check as PermissionRule;

    // Check 'not' conditions first
    if (rule.not && rule.not.length > 0) {
      if (this.hasAnyPermission(permissions, rule.not)) {
        return false;
      }
    }

    // Check 'allOf' conditions
    if (rule.allOf && rule.allOf.length > 0) {
      if (!this.hasAllPermissions(permissions, rule.allOf)) {
        return false;
      }
    }

    // Check 'anyOf' conditions
    if (rule.anyOf && rule.anyOf.length > 0) {
      if (!this.hasAnyPermission(permissions, rule.anyOf)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Context-aware permission checking for company-scoped operations
   */
  static canManageCompanyResource(
    userRole: UserRole | ExtendedUserRole | undefined,
    userCompanyId: string | null | undefined,
    targetCompanyId: string | null | undefined,
    permissions: string[] | null | undefined,
    permission: Permission
  ): boolean {
    if (!permissions || !userRole) return false;

    // Handle both string and object roles
    const roleName = typeof userRole === "string" ? userRole : userRole.name;

    // Super admin and platform admin can manage all resources
    if (roleName === "super_admin" || roleName === "platform_admin") {
      return this.hasPermission(permissions, permission);
    }

    // Company admin and company user can only manage resources in their company
    if (roleName === "company_admin" || roleName === "company_user") {
      if (!userCompanyId || !targetCompanyId) return false;
      if (userCompanyId !== targetCompanyId) return false;
      return this.hasPermission(permissions, permission);
    }

    // Regular user - check basic permission
    return this.hasPermission(permissions, permission);
  }

  /**
   * Legacy method for backward compatibility - no longer needed with simplified approach
   */
  static clearCache(): void {
    // No-op - no cache to clear in simplified approach
  }

  /**
   * Legacy method for backward compatibility
   */
  static getCacheStats() {
    return {
      size: 0,
      maxSize: 0,
      ttl: 0,
      timestampsCount: 0,
    };
  }
}

/**
 * Enhanced route configuration interface
 */
export interface RouteConfig {
  path: string;
  component: React.ComponentType;
  permissions?: PermissionCheck;
  fallbackRoute?: string;
  menu?: {
    label: string;
    icon: React.ComponentType;
    group?: "primary" | "secondary";
    order?: number;
  };
  children?: RouteConfig[];
}

/**
 * Hook to get current user's permissions (simplified)
 */
export function useUserPermissions(): string[] {
  const { permissions } = useAuthStore();
  return permissions || [];
}

/**
 * Optimized hook for permission checking with selective re-rendering
 */
export function usePermissionCheck(permissionsToCheck: PermissionCheck) {
  const { permissions, localUser } = useAuthStore();

  const permissionResult = useMemo(() => {
    return PermissionManager.checkPermission(permissions, permissionsToCheck);
  }, [permissions, permissionsToCheck]);

  return {
    hasPermission: permissionResult,
    userPermissions: permissions,
    userCompanyId: localUser?.companyId,
    // Utility functions that don't cause re-renders
    checkPermission: (check: PermissionCheck) =>
      PermissionManager.checkPermission(permissions, check),
    hasAnyPermission: (perms: Permission[]) =>
      PermissionManager.hasAnyPermission(permissions, perms),
    hasAllPermissions: (perms: Permission[]) =>
      PermissionManager.hasAllPermissions(permissions, perms),
  };
}

/**
 * Enhanced permission hook for component-level access control (simplified)
 */
export function usePermissions() {
  const { permissions, localUser } = useAuthStore();

  // Memoize the permission functions to avoid recreation on every render
  const permissionFunctions = useMemo(
    () => ({
      // Basic permission checking
      hasPermission: (permissionToCheck: Permission) =>
        PermissionManager.hasPermission(permissions, permissionToCheck),

      hasAnyPermission: (permissionsToCheck: Permission[]) =>
        PermissionManager.hasAnyPermission(permissions, permissionsToCheck),

      hasAllPermissions: (permissionsToCheck: Permission[]) =>
        PermissionManager.hasAllPermissions(permissions, permissionsToCheck),

      // Enhanced permission checking with complex rules
      checkPermission: (check: PermissionCheck) =>
        PermissionManager.checkPermission(permissions, check),

      // Get all user permissions
      getUserPermissions: () => permissions || [],
    }),
    [permissions]
  );

  return {
    ...permissionFunctions,
    // User info
    userRole: localUser?.role,
    userCompanyId: localUser?.companyId,
    userPermissions: permissions,
  };
}

/**
 * Hook for company-scoped permission checking
 */
export function useCompanyPermissions() {
  const { localUser, permissions } = useAuthStore();

  return useMemo(
    () => ({
      // Check if user can manage a resource in a specific company
      canManageCompanyResource: (
        targetCompanyId: string | null | undefined,
        permission: Permission
      ) =>
        PermissionManager.canManageCompanyResource(
          localUser?.role,
          localUser?.companyId,
          targetCompanyId,
          permissions,
          permission
        ),

      // Check if user can manage another user
      canManageUser: (targetUser: User) => {
        if (!localUser?.role || !permissions) return false;

        // Super admin can manage all users
        if (localUser.role?.name === "super_admin") {
          return PermissionManager.hasPermission(permissions, "manage_users");
        }

        // Platform admin can manage all users except super admins
        if (localUser.role?.name === "platform_admin") {
          if (targetUser.role?.name === "super_admin") return false;
          return PermissionManager.hasPermission(permissions, "manage_users");
        }

        // Company admin can only manage users in their company
        if (localUser.role?.name === "company_admin") {
          if (!localUser.companyId || !targetUser.company?.id) return false;
          if (localUser.companyId !== targetUser.company.id) return false;
          // Cannot manage other company admins or higher roles
          if (
            targetUser.role?.name &&
            ["super_admin", "platform_admin", "company_admin"].includes(
              targetUser.role.name
            )
          ) {
            return false;
          }
          return PermissionManager.hasPermission(permissions, "manage_users");
        }

        return false;
      },

      // Check if user can view company data
      canViewCompanyData: (targetCompanyId: string | null | undefined) => {
        if (!localUser?.role) return false;

        // Super admin and platform admin can view all companies
        if (
          localUser.role?.name &&
          ["super_admin", "platform_admin"].includes(localUser.role.name)
        ) {
          return true;
        }

        // Company users can only view their own company
        if (
          localUser.role?.name &&
          ["company_admin", "company_user"].includes(localUser.role.name)
        ) {
          return localUser.companyId === targetCompanyId;
        }

        return false;
      },

      // Get user's accessible company IDs
      getAccessibleCompanyIds: (): string[] => {
        if (!localUser?.role?.name) return [];

        // Super admin and platform admin can access all companies
        if (["super_admin", "platform_admin"].includes(localUser.role.name)) {
          return []; // Empty array means all companies
        }

        // Company users can only access their own company
        if (localUser.companyId) {
          return [localUser.companyId];
        }

        return [];
      },
    }),
    [localUser?.role, localUser?.companyId, permissions]
  );
}

/**
 * Hook to check route access with enhanced permission logic
 */
export function useCanAccessRoute(permissions?: PermissionCheck): boolean {
  const { checkPermission } = usePermissions();

  return useMemo(() => {
    if (!permissions) return true;
    return checkPermission(permissions);
  }, [permissions, checkPermission]);
}
